/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import store from '@/store';
import { USER } from '@/store/types';
import { showDialog, hideDialog, showToast } from '@/store/utils';
import { configWechatSDK } from '@/utils/connect';
import { isInWechatMobile } from '@/utils/environment';
import { WxTransferAuthPayload } from '@/api/types/common';
import { wechat } from 'vue-wechat/1.6.0';

const clearTradeInterval = (timer: number): void => clearInterval(timer);
const setTradeInterval = (outTradeNo, dialogId, resolve): number => {
  const timer = window.setInterval(() => {
    store.dispatch(`user/code/${USER.GET_ORDER}`, {
      id: outTradeNo,
      silent: true,
    }).then((res) => {
      if (res.data.state !== 0) {
        showToast({
          type: 'success',
          time: 2000,
          text: '已支付成功！',
        });
        hideDialog(dialogId);
        clearTradeInterval(timer);
        resolve();
      }
    });
  }, 1000);
  return timer;
};

const createPaymentTopup = (goal, fee): Promise<void> => new Promise((resolve) => {
  const params = {
    type: 'wxpay',
    fee,
    goal,
    attach: '',
  };
  let dialogId, timer;
  store.dispatch(`user/code/${USER.SET_ORDER}`, params).then((res) => {
    if (res) {
      dialogId = showDialog({
        type: 'alert',
        title: '微信扫码完成支付',
        content: `<img src=${res.data.qrcodeUrl} style="max-width: 100%;">`,
        isHTML: true,
        buttons: [{
          label: '已支付',
          primary: true,
          action: resolve,
        }],
        onclose: () => {
          clearTradeInterval(timer);
        },
      });
      timer = setTradeInterval(res.data.outTradeNo, dialogId, resolve);
    } else {
      createPaymentTopup(goal, fee).then(resolve);
    }
  });
});

export const requestMerchantTransferAuth = (auth: WxTransferAuthPayload): Promise<void> => new Promise((resolve, reject) => {
  if (!isInWechatMobile()) {
    showDialog({
      type: 'alert',
      title: '请在微信中打开',
      content: '请在微信中打开才能授权收款。',
    });
    reject();
    return;
  }
  configWechatSDK().then(() => {
    wechat.checkJsApi({
      jsApiList: ['requestMerchantTransfer'],
      success: (res) => {
        if (!res.checkResult || !res.checkResult.requestMerchantTransfer) {
          showDialog({
            type: 'alert',
            title: '微信版本过低',
            content: '你的微信版本过低，请更新至最新版本后再试。',
          });
          reject();
          return;
        }
        if (!window.WeixinJSBridge) {
          showDialog({
            type: 'alert',
            title: '微信环境异常',
            content: '无法调起微信确认收款，请刷新页面后再试。',
          });
          reject();
          return;
        }
        window.WeixinJSBridge.invoke('requestMerchantTransfer', auth, (response) => {
          const errMsg = response.err_msg || response.errMsg || '';
          if (errMsg === 'requestMerchantTransfer:ok') {
            window.setTimeout(resolve, 500);
          } else {
            reject(response);
          }
        });
      },
      fail: reject,
    });
  }).catch(reject);
});

const isMerchantTransferAuthRequired = (res): boolean =>
  res && res.errcode === 406 && res.data && res.data.action === 'wxTransferAuth' && res.data.auth;

export const createPayment = (dopayment: () => Promise<void | unknown>): Promise<void | unknown> => new Promise((resolve, reject) => {
  const onerror = (err): void => {
    const res = err && err.response;
    if (!res) {
      reject(err);
      return;
    }
    if (res.errcode === 445 || res.errcode === 446) {
      let resolved = false;
      showDialog({
        type: 'alert',
        title: '',
        content: res.errmsg,
        buttons: [{
          label: `微信支付${res.data.fee / 100}元`,
          primary: true,
          action: () => {
            resolved = true;
            createPaymentTopup(res.errcode === 445 ? 1 : 4, res.data.fee).then(() => {
              dopayment().then(resolve).catch(onerror);
            }).catch(reject);
          },
        }],
        onclose: () => {
          if (!resolved) {
            reject();
          }
        },
      });
    } else if (isMerchantTransferAuthRequired(res)) {
      requestMerchantTransferAuth(res.data.auth).then(() => {
        dopayment().then(resolve).catch(onerror);
      }).catch(reject);
    } else {
      reject(err);
    }
  };
  dopayment().then(resolve).catch(onerror);
});
